/**
 * Age-group → game format mapping (US Soccer Player Development Framework).
 *
 *   U6-U8   5v5   (4 outfield + 1 GK)
 *   U9-U10  7v7   (6 outfield + 1 GK)
 *   U11-U12 9v9   (8 outfield + 1 GK)
 *   U13+    11v11 (10 outfield + 1 GK)
 *
 * Used by the lineup pitch + tactic board to size player tokens and by
 * the session generator to cap drill players_required.
 */

export type GameFormat = '5v5' | '7v7' | '9v9' | '11v11';

export interface FormatInfo {
  format: GameFormat;
  starters: number;    // total on the field including GK
  outfield: number;    // outfield players (= starters - 1)
  defaultFormation: string;
}

const FORMAT_STARTERS: Record<GameFormat, number> = {
  '5v5': 5,
  '7v7': 7,
  '9v9': 9,
  '11v11': 11,
};

const DEFAULT_FORMATION_BY_FORMAT: Record<GameFormat, string> = {
  '5v5':  '1-2-2',
  '7v7':  '2-3-1',
  '9v9':  '3-3-2',
  '11v11': '4-3-3',
};

/** Pick the format for an age group string. Handles common shapes:
 *   "U8", "U10", "U11-U12", "U13-U14". Defaults to 11v11 when unknown. */
export function gameFormatForAge(ageGroup?: string | null): GameFormat {
  if (!ageGroup) return '11v11';
  const nums = ageGroup.match(/\d+/g)?.map(Number) ?? [];
  if (nums.length === 0) return '11v11';
  // Use the lowest age band as the driver (most conservative).
  const age = Math.min(...nums);
  if (age <= 8)  return '5v5';
  if (age <= 10) return '7v7';
  if (age <= 12) return '9v9';
  return '11v11';
}

export function formatInfoForAge(ageGroup?: string | null): FormatInfo {
  const format = gameFormatForAge(ageGroup);
  const starters = FORMAT_STARTERS[format];
  return {
    format,
    starters,
    outfield: starters - 1,
    defaultFormation: DEFAULT_FORMATION_BY_FORMAT[format],
  };
}

/** Number of players a typical DRILL should scope to for an age group.
 *  Useful for sizing small-sided games in the session planner so a U10
 *  team doesn't end up with a 9v9 rondo. */
export function typicalDrillPlayersForAge(ageGroup?: string | null): number {
  const { starters } = formatInfoForAge(ageGroup);
  // Two small-sided groups ≈ full roster; most drills are half that.
  return Math.max(4, Math.min(starters * 2, 16));
}
